import {ResourceType} from '../../../@types/resource-type';
import TabItem from '../../../@types/tab-item';
import {getBreadCrumbsProps} from '../../../utils/resource-utils';
import {getTabLabel} from './tab-editor-config';
import {uuid} from '../../../utils/utils';

/**
 * Resolves additional information for a tab object based on its resource type.
 * This adds context like safety models to event sequences.
 */
export function resolveAdditionalInformation(
    tabObject: { objectType: ResourceType, object: any },
    getAdditionalResource: (type: ResourceType) => any
): any {
    const { objectType, object } = tabObject;
    
    // Event sequence types need their parent safety model
    if (objectType === ResourceType.EVENT_SEQUENCES || 
        objectType === ResourceType.EVENT_SEQUENCE_TREES ||
        objectType === ResourceType.EVENT_SEQUENCE_UNCERTAINTY_ANALYSIS || 
        objectType === ResourceType.EVENT_SEQUENCE_SENSITIVIY_ANALYSIS) {
        
        const newObject = Object.assign({}, object);
        const safetyModels = getAdditionalResource(ResourceType.SAFETY_MODELS);
        newObject.safetyModel = safetyModels?.resources?.find(
            (safetyModel: any) => safetyModel.id === object.containerId
        );
        return newObject;
    }
    
    // Event sequence history trees have a special structure
    if (objectType === ResourceType.EVENT_SEQUENCE_HISTORY_TREES) {
        const newObject = Object.assign({}, object);
        const esd = object.eventSequenceDTO;
        const safetyModels = getAdditionalResource(ResourceType.SAFETY_MODELS);
        
        esd.safetyModel = safetyModels?.resources?.find(
            (safetyModel: any) => safetyModel.id === esd.containerId
        );
        
        if (!esd.group && esd.safetyModel) {
            esd.group = esd.safetyModel.group;
        }
        
        newObject.eventSequenceDTO = esd;
        return newObject;
    }
    
    return object;
}

/**
 * Resolves the tab label for a given object based on its resource type
 */
export function resolveTabLabel(
    objectType: ResourceType,
    object: any
): string {
    return getTabLabel(objectType, object);
}

/**
 * Creates a new tab item with all necessary properties
 */
export function createNewTab(
    objectType: ResourceType,
    object: any,
    currentTabCount: number,
    getAdditionalResource?: (type: ResourceType) => any
): TabItem {
    const objectWithAdditionalInfo = getAdditionalResource
        ? resolveAdditionalInformation({ objectType, object }, getAdditionalResource)
        : object;

    const newTab = new TabItem(
        objectType,
        object.id ?? object.uniqueId ?? null,
        currentTabCount,
        currentTabCount,
        resolveTabLabel(objectType, objectWithAdditionalInfo),
        getBreadCrumbsProps(objectWithAdditionalInfo, objectType)
    );

    newTab.objectToEdit = objectWithAdditionalInfo;

    // Generate a stable unique identifier for this tab.
    // This uid is used as the React key when rendering tabs, ensuring that
    // closing one tab doesn't cause other tabs to unmount/remount due to index changes.
    newTab.uid = uuid();

    return newTab;
}

/**
 * Removes a tab and reindexes remaining tabs
 */
export function removeTabAndReindex(
    tabs: TabItem[],
    tabToDelete: TabItem,
    currentActiveIndex: number
): { newTabs: TabItem[], newActiveIndex: number } {
    const newTabs: TabItem[] = [];

    // Filter out the deleted tab and reindex
    tabs.filter((tab) => tab.index !== tabToDelete.index).forEach((tab) => {
        const adjustedTab = Object.assign({}, tab);
        if (adjustedTab.index > tabToDelete.index) {
            adjustedTab.index = adjustedTab.index - 1;
        }
        newTabs.push(adjustedTab);
    });

    // Calculate the new active tab index
    let newActiveIndex: number;
    if (tabToDelete.index === currentActiveIndex) {
        // Closing the active tab - switch to previous (or first if closing first)
        newActiveIndex = currentActiveIndex === 0 ? 0 : currentActiveIndex - 1;
    } else if (tabToDelete.index < currentActiveIndex) {
        // Closing a tab before the active one - adjust index down by 1
        newActiveIndex = currentActiveIndex - 1;
    } else {
        // Closing a tab after the active one - keep same index
        newActiveIndex = currentActiveIndex;
    }

    // Ensure index is within bounds
    if (newActiveIndex >= newTabs.length) {
        newActiveIndex = newTabs.length > 0 ? newTabs.length - 1 : 0;
    }

    return { newTabs, newActiveIndex };
}

/**
 * Updates a tab in the tabs array
 */
export function updateTabInArray(
    tabs: TabItem[],
    tabToUpdate: TabItem,
    updates: Partial<TabItem>
): TabItem[] {
    return tabs.map(tab => {
        if (tab.type === tabToUpdate.type && tab.index === tabToUpdate.index) {
            // Create a new TabItem instance with updated properties
            const updatedTab = new TabItem(
                tabToUpdate.type,
                updates.docId ?? tabToUpdate.docId ?? 0,
                tabToUpdate.index,
                tabToUpdate.value ?? tabToUpdate.index,
                updates.label ?? tabToUpdate.label,
                updates.breadCrumbs ?? tabToUpdate.breadCrumbs
            );
            updatedTab.objectToEdit = updates.objectToEdit ?? tabToUpdate.objectToEdit;
            // CRITICAL: Preserve uid to maintain stable React key (prevents unmount/remount)
            updatedTab.uid = tabToUpdate.uid;
            return updatedTab;
        }
        // Create a copy of the existing tab
        const copiedTab = new TabItem(
            tab.type,
            tab.docId ?? 0,
            tab.index,
            tab.value ?? tab.index,
            tab.label,
            tab.breadCrumbs
        );
        copiedTab.objectToEdit = tab.objectToEdit;
        // CRITICAL: Preserve uid to maintain stable React key (prevents unmount/remount)
        copiedTab.uid = tab.uid;
        return copiedTab;
    });
}

/**
 * Filters tabs that should be removed when a workspace object is deleted
 */
export function filterTabsAfterDeletion(
    tabs: TabItem[],
    deletedObjectId: string | number,
    deletedChildrenIds: (string | number)[] = []
): TabItem[] {
    const idsToRemove = [deletedObjectId, ...deletedChildrenIds];
    
    const remainingTabs = tabs.filter((tab) => {
        // Check if this tab should be removed
        let shouldRemove = idsToRemove.some((id) => 
            tab.docId == id || tab.objectToEdit.group?.id == id
        );
        
        // Special handling for EVENT_SEQUENCE related tabs
        // When an EVENT_SEQUENCE is deleted, also remove related tabs (trees, history, etc.)
        if (!shouldRemove && idsToRemove.includes(deletedObjectId)) {
            // Check if this is a related tab type that references the same ID
            const relatedEventSequenceTypes = [
                ResourceType.EVENT_SEQUENCES,
                ResourceType.EVENT_SEQUENCE_TREES,
                ResourceType.EVENT_SEQUENCE_HISTORY,
                ResourceType.EVENT_SEQUENCE_HISTORY_TREES,
                ResourceType.EVENT_SEQUENCE_UNCERTAINTY_ANALYSIS,
                ResourceType.EVENT_SEQUENCE_SENSITIVIY_ANALYSIS
            ];
            
            if (relatedEventSequenceTypes.includes(tab.type) && tab.docId == deletedObjectId) {
                shouldRemove = true;
            }
            // A Component version tab is keyed by its history entry, not by the Component.
            if (tab.type === ResourceType.COMPONENT_HISTORY_TREE && tab.objectToEdit.component?.id == deletedObjectId) {
                shouldRemove = true;
            }
        }
        
        
        return !shouldRemove;
    });
    
    // Reindex remaining tabs by creating new TabItem instances
    return remainingTabs.map((tab, index) => {
        const reindexedTab = new TabItem(
            tab.type,
            tab.docId ?? 0,
            index,
            index,
            tab.label,
            tab.breadCrumbs
        );
        reindexedTab.objectToEdit = tab.objectToEdit;
        // CRITICAL: Preserve uid to maintain stable React key (prevents unmount/remount)
        reindexedTab.uid = tab.uid;
        return reindexedTab;
    });
}

/**
 * Synchronizes related tabs when an object is updated
 * (e.g., when an event sequence is updated, related tree and history tabs need updating)
 */
export function synchronizeRelatedTabs(
    tabs: TabItem[],
    updatedObject: { objectType: ResourceType, object: any }
): TabItem[] {
    const { objectType, object } = updatedObject;
    
    // Handle simple resource types (Groups, Safety Models, etc.)
    if (objectType === ResourceType.GROUPS || 
        objectType === ResourceType.SAFETY_MODELS ||
        objectType === ResourceType.WORKSHOPS ||
        objectType === ResourceType.RISK_FORECASTS ||
        objectType === ResourceType.OPERATIONAL_CHANGES ||
        objectType === ResourceType.PERFORMANCE_SHAPING_FACTORS) {
        
        return tabs.map(tab => {
            if (tab.type === objectType && tab.docId == object.id) {
                // Update the tab with new data
                const newLabel = resolveTabLabel(tab.type, object);
                const newBreadCrumbs = getBreadCrumbsProps(object, tab.type);
                
                const updatedTab = new TabItem(
                    tab.type,
                    tab.docId ?? 0,
                    tab.index,
                    tab.value ?? tab.index,
                    newLabel,
                    newBreadCrumbs
                );
                updatedTab.objectToEdit = object;
                // CRITICAL: Preserve uid to maintain stable React key (prevents unmount/remount)
                updatedTab.uid = tab.uid;
                return updatedTab;
            }
            return tab;
        });
    }

    if (objectType === ResourceType.EVENT_SEQUENCES) {
        // Update all tabs related to this event sequence
        return tabs.map(tab => {
            if (tab.docId === object.id) {
                // FIX: Determine the correct object data for this tab type
                let updatedTabObject = tab.objectToEdit;
                
                if (tab.type === ResourceType.EVENT_SEQUENCES) {
                    // For EVENT_SEQUENCES tab, use the full updated object
                    updatedTabObject = object;
                } else if (tab.type === ResourceType.EVENT_SEQUENCE_TREES) {
                    // For tree view, update the eventSequence property with new data
                    // Also update the name at the root level since that's what getTabLabel looks for
                    updatedTabObject = { 
                        ...tab.objectToEdit, 
                        eventSequence: object,
                        name: object.name // CRITICAL: Update name at root level for label
                    };
                } else if (tab.type === ResourceType.EVENT_SEQUENCE_HISTORY) {
                    // For history view, use the full updated object
                    updatedTabObject = object;
                }
                
                // Now use the UPDATED object for the label
                const newLabel = resolveTabLabel(tab.type, updatedTabObject);
                let newBreadCrumbs = tab.breadCrumbs;
                
                if (tab.type === ResourceType.EVENT_SEQUENCE_TREES ||
                    tab.type === ResourceType.EVENT_SEQUENCE_HISTORY) {
                    newBreadCrumbs = getBreadCrumbsProps(updatedTabObject, tab.type);
                }
                
                const updatedTab = new TabItem(
                    tab.type,
                    tab.docId ?? 0,
                    tab.index,
                    tab.value ?? tab.index,
                    newLabel,
                    newBreadCrumbs
                );
                updatedTab.objectToEdit = updatedTabObject;
                // CRITICAL: Preserve uid to maintain stable React key (prevents unmount/remount)
                updatedTab.uid = tab.uid;
                return updatedTab;
            }
            return tab;
        });
    }

    if (objectType === ResourceType.EVENT_SEQUENCE_TREES) {
        // Update all tabs related to this event sequence tree
        return tabs.map(tab => {
            if (tab.docId === object.id) {
                // FIX: Determine the correct object data for this tab type
                let updatedTabObject = tab.objectToEdit;
                
                if (tab.type === ResourceType.EVENT_SEQUENCE_TREES) {
                    // For tree tab, use the full updated object
                    updatedTabObject = object;
                } else if (tab.type === ResourceType.EVENT_SEQUENCES) {
                    // For summary tab, extract the eventSequence from the tree object
                    updatedTabObject = object.eventSequence || tab.objectToEdit;
                } else if (tab.type === ResourceType.EVENT_SEQUENCE_HISTORY) {
                    // For history tab, use the eventSequence from the tree object
                    updatedTabObject = object.eventSequence || tab.objectToEdit;
                }
                
                // Now use the UPDATED object for the label
                const newLabel = resolveTabLabel(tab.type, updatedTabObject);
                const newBreadCrumbs = getBreadCrumbsProps(updatedTabObject, tab.type);

                const updatedTab = new TabItem(
                    tab.type,
                    tab.docId ?? 0,
                    tab.index,
                    tab.value ?? tab.index,
                    newLabel,
                    newBreadCrumbs
                );
                updatedTab.objectToEdit = updatedTabObject;
                // CRITICAL: Preserve uid to maintain stable React key (prevents unmount/remount)
                updatedTab.uid = tab.uid;

                return updatedTab;
            }
            return tab;
        });
    }

    return tabs;
}